import "@/style/index.css";
import "reactflow/dist/style.css";

import * as Sentry from "@sentry/browser";

import App from "@/App";
import PwaInstall from "@/components/Common/PwaInstall";
import { AuthContextType, AuthUserContext } from "@/hooks/useAuthUser";
import { initI18n } from "@/i18n";
import { pluginUI } from "@/plugin-ui";
import type { PluginUI } from "@/plugin-ui-contract";
import { PlugConfigMeta } from "@/types/plugConfig";
import careConfig from "@careConfig";
import i18next from "i18next";
import React, { Context } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import { registerSW } from "virtual:pwa-register";
import { initZod } from "./lib/zod";

// Extend Window interface to include CARE_API_URL
declare global {
  interface Window {
    CARE_API_URL: string;
    CARE_UI: PluginUI;
    __CORE_ENV__: typeof careConfig;
    __CARE_PLUGIN_RUNTIME__: { meta: PlugConfigMeta };
    AuthUserContext: Context<AuthContextType | null>;
  }
}

// Expose Environment variable to window object for use in plugins
window.CARE_UI = pluginUI;
window.CARE_API_URL = careConfig.apiUrl;
window.AuthUserContext = AuthUserContext;
window.__CORE_ENV__ = careConfig;

if ("serviceWorker" in navigator) {
  const update = registerSW({
    immediate: false,
    onNeedRefresh() {
      toast(i18next.t("pwa_update"), {
        description: i18next.t("pwa_update_save"),
        duration: Infinity,
        action: {
          label: i18next.t("pwa_reload"),
          onClick: () => {
            void update(true);
          },
        },
        cancel: { label: i18next.t("pwa_later"), onClick: () => {} },
      });
    },
  });
}

if (import.meta.env.PROD) {
  Sentry.init({
    environment: import.meta.env.MODE,
    dsn: "https://8801155bd0b848a09de9ebf6f387ebc8@sentry.io/5183632",
  });
}

function initApp() {
  const container = document.getElementById("root");

  if (!container) {
    throw new Error("Root container not found");
  }

  const root = createRoot(container);

  root.render(
    <React.StrictMode>
      <App />
      <PwaInstall />
    </React.StrictMode>,
  );
}

// Initialize i18n with namespaces from API before rendering the app
initI18n()
  .catch((error) => {
    console.error("Failed to initialize i18n:", error);
  })
  .finally(() => {
    initZod();
    initApp();
  });
